import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CreateJobModal from '../components/jobs/CreateJobModal';
import { useJobStore } from '../stores/jobStore';
import { useProfileStore } from '../stores/profileStore';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '../components/LoadingSpinner';
import JobDetailsModal from '../components/jobs/JobDetailsModal';
import BidderDetailsModal from '../components/quotes/BidderDetailsModal';
import bgImageUrl from '../assets/images/arrierePlan2.png';
import { decodeModalParams } from '../utils/modalUrlParams';
import { Job } from '../types/job';
import JobListCard from '../components/jobs/JobListCard';
import { readJobCreationSession } from '../components/jobs/jobCreation/jobCreationSession';

const STEP_KEYS = ['one', 'two', 'three'] as const;
const MOBILE_INITIAL_VISIBLE_JOBS = 3;
const MOBILE_VISIBLE_INCREMENT = 3;

const isMobileViewport = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia('(max-width: 767px)').matches;
};

interface JobsLocationState {
  selected_id?: string;
  openCreate?: boolean;
}

const Jobs: React.FC = () => {
  const { t } = useTranslation('jobs');
  const { loading, jobs, fetchJobs } = useJobStore();
  const { profile } = useProfileStore();
  const [quoteIdToShow, setQuoteIdToShow] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [mobileVisibleJobs, setMobileVisibleJobs] = useState(MOBILE_INITIAL_VISIBLE_JOBS);
  const [mobileLayoutEnabled, setMobileLayoutEnabled] = useState(isMobileViewport);
  const [showCreateJobModal, setShowCreateJobModal] = useState(false);

  const location = useLocation();
  const locationState = (location.state as JobsLocationState | null) ?? null;
  const sharedQuoteId = locationState?.selected_id;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  //use new variable to avoid conflict with other modals
  const findQuoteToShow = jobs.find(job => job.quotes?.some(quote => quote.id === quoteIdToShow));

  useEffect(() => {
    if (sharedQuoteId) {
      setQuoteIdToShow(sharedQuoteId);
    }
  }, [sharedQuoteId]);

  useEffect(() => {
    if (locationState?.openCreate) {
      setShowCreateJobModal(true);
    }
  }, [locationState?.openCreate]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const onViewportChange = (event: MediaQueryListEvent) => {
      setMobileLayoutEnabled(event.matches);
    };

    setMobileLayoutEnabled(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onViewportChange);
      return () => mediaQuery.removeEventListener('change', onViewportChange);
    }

    mediaQuery.addListener(onViewportChange);
    return () => mediaQuery.removeListener(onViewportChange);
  }, []);

  useEffect(() => {
    setMobileVisibleJobs(MOBILE_INITIAL_VISIBLE_JOBS);
  }, [jobs.length]);

  // Preserve the legacy draft-publication return route before considering the
  // new wizard session. Existing Stripe returns rely on JobDetailsPanel to
  // reopen this modal at the project hub.
  useEffect(() => {
    const modalParams = decodeModalParams(searchParams);
    if (modalParams?.type === 'publishJob' && modalParams.jobId) {
      navigate(`/projects/${modalParams.jobId}?openModal=publishJob&jobId=${modalParams.jobId}`, {
        replace: true,
      });
      return;
    }

    // The global wallet owns its payment success/cancelled UI. Once that query
    // flag is gone, restore the per-user creation session on /jobs.
    if (!profile?.id || searchParams.get('purchase')) return;
    if (readJobCreationSession(profile.id)) setShowCreateJobModal(true);
  }, [navigate, profile?.id, searchParams]);

  const handleJobCreated = (jobId: string) => {
    setShowCreateJobModal(false);
    navigate(`/projects/${jobId}`, { replace: true });
  };

  const openCreateModal = () => {
    setShowCreateJobModal(true);
  };

  const openManageJob = (job: Job) => {
    navigate(`/projects/${job.id}`);
  };

  const stepEntries = useMemo(
    () =>
      STEP_KEYS.map((key, index) => ({
        id: index + 1,
        title: t(`bigSteps.steps.${key}.title`),
        description: t(`bigSteps.steps.${key}.description`),
      })),
    [t]
  );

  const currentStep = stepEntries[currentStepIndex] ?? stepEntries[0];
  const hasMoreMobileJobs = jobs.length > mobileVisibleJobs;
  const mobileJobs = jobs.slice(0, mobileVisibleJobs);
  const jobsToRender = mobileLayoutEnabled ? mobileJobs : jobs;

  const goToPreviousStep = () => {
    if (stepEntries.length === 0) return;
    setCurrentStepIndex(prev => (prev === 0 ? stepEntries.length - 1 : prev - 1));
  };

  const goToNextStep = () => {
    if (stepEntries.length === 0) return;
    setCurrentStepIndex(prev => (prev === stepEntries.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="mb-8 flex flex-col gap-6 px-4 md:min-h-[calc(100vh-4rem)] md:px-5 lg:px-32">
      <div className="flex w-full flex-col pb-2 pt-4">
        <h1 className="text-[45px] font-semibold leading-[55px] text-[#304659] md:text-[95px] md:leading-[100px]">
          {t('title')}
        </h1>
      </div>

      <section className="overflow-hidden rounded-[25px] bg-white shadow-[0px_3px_12px_rgba(13,45,57,0.14)]">
        <div className="relative">
          <img
            src={bgImageUrl}
            alt={t('title')}
            className="h-[450px] w-full object-cover md:h-[327px]"
          />

          <div className="absolute left-4 right-4 top-4 rounded-[25px] bg-white p-4 shadow-[0px_3px_12px_rgba(13,45,57,0.14)] md:left-auto md:right-8 md:top-3 md:w-[390px] md:p-6">
            <h2 className="text-[25px] font-semibold leading-[35px] text-[#304659]">
              {t('create_offer_title', { defaultValue: 'Créer une nouvelle offre d’emploi' })}
            </h2>
            <p className="mt-2 text-[15px] leading-5 text-[#304659]">
              {t('create_offer_description', {
                defaultValue:
                  'Recruter une personne passionnée pour un projet clé. Créer dès maintenant votre offre d’emploi et faites la différence !',
              })}
            </p>
            <button
              type="button"
              onClick={openCreateModal}
              className="mt-4 w-full rounded-full bg-primary px-4 py-2 text-[17px] font-bold leading-[25px] text-white transition-opacity hover:opacity-90"
            >
              {t('create_offer_button')}
            </button>
          </div>
        </div>

        <div className="bg-white px-4 py-6 md:px-8">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-[25px] font-bold leading-[30px] text-[#304659]">
                {t('bigSteps.title')}
              </h2>
              <p className="mt-1 text-[15px] leading-5 text-[#304659]">{t('bigSteps.subtitle')}</p>
            </div>
          </div>

          <div className="mt-4 hidden grid-cols-3 gap-6 md:grid">
            {stepEntries.map(step => (
              <article key={step.id} className="flex gap-3">
                <p className="text-[70px] font-black leading-[75px] text-[#304659]">{step.id}</p>
                <div className="pt-2">
                  <h3 className="text-[17px] font-bold leading-[25px] text-[#304659]">
                    {step.title}
                  </h3>
                  <p className="mt-1 text-[15px] leading-5 text-[#3B5266]">{step.description}</p>
                </div>
              </article>
            ))}
          </div>

          {currentStep && (
            <div className="mt-4 md:hidden">
              <article className="flex gap-3">
                <p className="text-[70px] font-black leading-[75px] text-[#304659]">
                  {currentStep.id}
                </p>
                <div className="pt-2">
                  <h3 className="text-[17px] font-bold leading-[25px] text-[#304659]">
                    {currentStep.title}
                  </h3>
                  <p className="mt-1 text-[15px] leading-5 text-[#3B5266]">
                    {currentStep.description}
                  </p>
                </div>
              </article>
              <div className="mt-4 flex justify-center">
                <div className="inline-flex overflow-hidden rounded-full">
                  <button
                    type="button"
                    onClick={goToPreviousStep}
                    className="flex h-9 w-11 items-center justify-center bg-primary/30 text-primary transition-opacity hover:opacity-90"
                    aria-label={t('steps_previous', { defaultValue: 'Previous step' })}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path
                        d="M8.75 3.5L5.25 7L8.75 10.5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={goToNextStep}
                    className="flex h-9 w-11 items-center justify-center bg-primary text-white transition-opacity hover:opacity-90"
                    aria-label={t('steps_next', { defaultValue: 'Next step' })}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path
                        d="M5.25 3.5L8.75 7L5.25 10.5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* This is hidden for now as we don't have a need help button yet */}
          <div className="mt-4 hidden items-center justify-end gap-3 md:flex md:hidden">
            <button
              type="button"
              className="h-10 rounded-full border-2 border-primary/30 px-6 text-[17px] font-semibold leading-[25px] text-primary transition-opacity hover:opacity-90"
            >
              {t('bigSteps.needHelp')}
            </button>
            <button
              type="button"
              onClick={openCreateModal}
              className="h-10 rounded-full bg-primary px-6 text-[17px] font-semibold leading-[25px] text-white transition-opacity hover:opacity-90"
            >
              {t('bigSteps.cta')}
            </button>
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <p className="text-[13px] font-semibold uppercase tracking-[1px] text-[#304659]">
          {t('published_jobs_title', { defaultValue: 'Mes offres d’emploi publiées' })}
        </p>

        {loading && jobs.length === 0 && <LoadingSpinner />}

        {!loading && jobs.length === 0 && (
          <div className="rounded-[18px] border-2 border-[#BFD3DE] bg-white px-4 py-6 text-center text-[#304659]">
            {t('noJobs')}
          </div>
        )}

        <div className="flex flex-col gap-4">
          {jobsToRender.map((job, index) => (
            <JobListCard key={job.id} job={job} index={index} onManageClick={openManageJob} />
          ))}
          {mobileLayoutEnabled && hasMoreMobileJobs && (
            <button
              type="button"
              onClick={() => setMobileVisibleJobs(prev => prev + MOBILE_VISIBLE_INCREMENT)}
              className="h-10 rounded-full bg-primary px-6 text-[17px] font-semibold leading-[25px] text-white transition-opacity hover:opacity-90"
            >
              {t('loadMore', { defaultValue: 'Load more' })}
            </button>
          )}
        </div>
      </section>
      {showCreateJobModal && (
        <CreateJobModal
          onJobCreated={handleJobCreated}
          onClose={() => setShowCreateJobModal(false)}
        />
      )}

      {sharedQuoteId && findQuoteToShow && (
        <>
          {findQuoteToShow.quotes && findQuoteToShow.quotes.length > 0 ? (
            <BidderDetailsModal
              onClose={() => {
                setQuoteIdToShow(null);
                navigate('/jobs', { replace: true });
              }}
              job={findQuoteToShow}
              quote={findQuoteToShow.quotes.find(quote => quote.id === quoteIdToShow)!}
            />
          ) : (
            <JobDetailsModal job={findQuoteToShow} onClose={() => setQuoteIdToShow(null)} />
          )}
        </>
      )}
    </div>
  );
};

export default Jobs;
